/**
 * Cancellation for work that is SHARED by several consumers.
 *
 * A coalesced fetch is one in-flight promise with N awaiters, so a consumer's
 * own `AbortSignal` cannot simply be forwarded to it: aborting when the first
 * consumer goes away would cancel a request the other N-1 are still waiting on.
 * This aggregates the consumers instead — the derived `signal` aborts only once
 * EVERY joined consumer has aborted, which is exactly "nobody is waiting for
 * this any more".
 *
 * A consumer that joins without a signal is uncancellable and pins the work for
 * good. That is the deliberate default: every caller that does not opt in keeps
 * the previous run-to-completion behaviour.
 */
export class SharedCancellation {
  private readonly controller = new AbortController();
  // Consumers that have not (and, for signal-less joins, cannot) abort.
  private live = 0;

  get signal(): AbortSignal {
    return this.controller.signal;
  }

  /**
   * True once every joined consumer has aborted. A spent instance must never be
   * handed to a new consumer — its signal is already aborted, so the shared
   * promise is failing — which is why the coalescing maps check this before
   * reusing an entry.
   */
  get aborted(): boolean {
    return this.controller.signal.aborted;
  }

  join(signal?: AbortSignal): void {
    if (!signal) {
      // Never decremented: an uncancellable consumer holds the count above zero
      // for the lifetime of the work.
      this.live += 1;
      return;
    }
    if (signal.aborted) {
      // Joined by a consumer that already gave up: contributes nothing, but can
      // still be the last one, so re-evaluate.
      this.settle();
      return;
    }
    this.live += 1;
    signal.addEventListener(
      'abort',
      () => {
        this.live -= 1;
        this.settle();
      },
      { once: true }
    );
  }

  private settle(): void {
    if (this.live === 0) this.controller.abort();
  }
}
